import { useState } from "react";
import { Button, Dialog, Field, Input, Select, Switch } from "../../ds/DS";
import { api } from "../../lib/api";
import {
  STAGES,
  STAGE_LABELS,
  STATUS_LABELS,
  type Stage,
  type ProjectStatus,
} from "../../types";
import type { VClient, VProject } from "../data";
import { DialogFoot } from "./parts";

const STATUS_KEYS: ProjectStatus[] = ["active", "on_hold", "complete", "cancelled"];
const stageLabel = (s: Stage) => STAGE_LABELS[s];
const stageFromLabel = (l: string) =>
  (STAGES.find((s) => STAGE_LABELS[s] === l) ?? "brief") as Stage;
const statusLabel = (s: ProjectStatus) => STATUS_LABELS[s];
const statusFromLabel = (l: string) =>
  (STATUS_KEYS.find((s) => STATUS_LABELS[s] === l) ?? "active") as ProjectStatus;

const toDateInput = (ms: number | null) =>
  ms ? new Date(ms).toISOString().slice(0, 10) : "";
const fromDateInput = (v: string) => (v ? Date.parse(v) : null);

export function ProjectEditDialog({
  open,
  project,
  onClose,
  onSaved,
}: {
  open: boolean;
  project: VProject;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState(() => ({
    title: project.raw.title,
    code: project.code,
    summary: project.summary,
    stage: stageLabel(project.raw.stage),
    status: statusLabel(project.raw.status),
    health: project.health,
    progress: String(project.progress),
    due: toDateInput(project.raw.dueAt),
    started: toDateInput(project.raw.startedAt),
    hoursUsed: project.raw.hoursUsed == null ? "" : String(project.raw.hoursUsed),
    hoursBudget:
      project.raw.hoursBudget == null ? "" : String(project.raw.hoursBudget),
    lead: project.lead,
    stack: project.stack.join(", "),
    cadence: project.cadence,
    risk: project.risk,
  }));
  const [signoff, setSignoff] = useState(project.requiresSignoff);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.title.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, {
        title: f.title.trim(),
        code: f.code.trim(),
        summary: f.summary,
        stage: stageFromLabel(f.stage),
        status: statusFromLabel(f.status),
        health: f.health,
        progress: Number(f.progress) || 0,
        due_at: fromDateInput(f.due),
        started_at: fromDateInput(f.started),
        hours_used: f.hoursUsed === "" ? null : Number(f.hoursUsed),
        hours_budget: f.hoursBudget === "" ? null : Number(f.hoursBudget),
        lead: f.lead,
        stack: f.stack
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        requires_signoff: signoff,
        cadence: f.cadence,
        risk: f.risk,
      });
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title="Edit project"
      description="Details clients see across the portal."
      accent
      width={640}
      onClose={onClose}
      footer={
        <DialogFoot
          saveLabel="Save"
          busy={busy}
          invalid={!f.title.trim()}
          onCancel={onClose}
          onSave={() => void save()}
        />
      }
    >
      <div className="stack-16">
        <Field label="Name" required>
          <Input value={f.title} onChange={(e) => set("title", e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field label="Code" hint="Short reference, e.g. NG-SYNC">
            <Input value={f.code} onChange={(e) => set("code", e.target.value)} />
          </Field>
          <Field label="Lead">
            <Input value={f.lead} onChange={(e) => set("lead", e.target.value)} />
          </Field>
        </div>
        <Field label="Summary" hint="One line shown on the project card.">
          <Input
            value={f.summary}
            onChange={(e) => set("summary", e.target.value)}
          />
        </Field>
        <div className="grid-2">
          <Field label="Stage">
            <Select
              options={STAGES.map(stageLabel)}
              value={f.stage}
              onChange={(e) => set("stage", e.target.value)}
            />
          </Field>
          <Field label="Status">
            <Select
              options={STATUS_KEYS.map(statusLabel)}
              value={f.status}
              onChange={(e) => set("status", e.target.value)}
            />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Health">
            <Select
              options={["On track", "At risk"]}
              value={f.health === "at-risk" ? "At risk" : "On track"}
              onChange={(e) =>
                set("health", e.target.value === "At risk" ? "at-risk" : "on-track")
              }
            />
          </Field>
          <Field label="Progress %">
            <Input
              type="number"
              min={0}
              max={100}
              value={f.progress}
              onChange={(e) => set("progress", e.target.value)}
            />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Started">
            <Input
              type="date"
              value={f.started}
              onChange={(e) => set("started", e.target.value)}
            />
          </Field>
          <Field label="Due">
            <Input
              type="date"
              value={f.due}
              onChange={(e) => set("due", e.target.value)}
            />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Hours used">
            <Input
              type="number"
              step="0.5"
              value={f.hoursUsed}
              onChange={(e) => set("hoursUsed", e.target.value)}
            />
          </Field>
          <Field label="Hours budget">
            <Input
              type="number"
              step="0.5"
              value={f.hoursBudget}
              onChange={(e) => set("hoursBudget", e.target.value)}
            />
          </Field>
        </div>
        <Field
          label="Risk"
          hint="Shown as an amber note on the project card. Leave blank for none."
        >
          <Input
            multiline
            rows={2}
            value={f.risk}
            onChange={(e) => set("risk", e.target.value)}
          />
        </Field>
        <Field label="Stack" hint="Comma-separated, e.g. Shopify, Xero, AWS Lambda">
          <Input value={f.stack} onChange={(e) => set("stack", e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field label="Release cadence" hint="Shown on the release board.">
            <Input
              placeholder="Fortnightly, Tuesdays"
              value={f.cadence}
              onChange={(e) => set("cadence", e.target.value)}
            />
          </Field>
          <Field
            label="Client signs off releases"
            hint={
              signoff
                ? "Releases go live only after the client approves them on sandbox."
                : "Releases go live once they pass on sandbox."
            }
          >
            <Switch
              checked={signoff}
              onChange={setSignoff}
              label="Client signs off releases"
            />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

export function ProjectCreateDialog({
  open,
  clientId,
  onClose,
  onSaved,
}: {
  open: boolean;
  clientId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState({ title: "", code: "", summary: "" });
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.title.trim() || !clientId) return;
    setBusy(true);
    try {
      await api.post("/projects", {
        clientId,
        title: f.title.trim(),
        code: f.code.trim(),
        summary: f.summary,
      });
      onSaved();
      onClose();
      setF({ title: "", code: "", summary: "" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title="New project"
      description="Create the project, then open it to fill in stage, dates and stack."
      accent
      width={520}
      onClose={onClose}
      footer={
        <div className="row-8">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || !f.title.trim()}>
            {busy ? "Creating…" : "Create project"}
          </Button>
        </div>
      }
    >
      <div className="stack-16">
        <Field label="Name" required>
          <Input
            value={f.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="Xero ↔ Shopify sync"
          />
        </Field>
        <Field label="Code" hint="Short reference, e.g. NG-SYNC">
          <Input value={f.code} onChange={(e) => set("code", e.target.value)} />
        </Field>
        <Field label="Summary">
          <Input
            value={f.summary}
            onChange={(e) => set("summary", e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

export function ClientEditDialog({
  open,
  client,
  onClose,
  onSaved,
}: {
  open: boolean;
  client: VClient;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState(() => ({
    name: client.name,
    contact: client.contact,
    plan: client.plan,
    since: client.since,
  }));
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/clients/${client.id}`, {
        name: f.name.trim(),
        contact: f.contact,
        plan: f.plan,
        since: f.since,
      });
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title="Edit client"
      accent
      width={520}
      onClose={onClose}
      footer={
        <div className="row-8">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || !f.name.trim()}>
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      }
    >
      <div className="stack-16">
        <Field label="Name" required>
          <Input value={f.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label="Main contact">
          <Input
            value={f.contact}
            onChange={(e) => set("contact", e.target.value)}
          />
        </Field>
        <div className="grid-2">
          <Field label="Plan" hint="e.g. Retainer · 20h/mo">
            <Input value={f.plan} onChange={(e) => set("plan", e.target.value)} />
          </Field>
          <Field label="Client since" hint="e.g. Mar 2025">
            <Input value={f.since} onChange={(e) => set("since", e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

export function ClientCreateDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (newClientId?: string) => void;
}) {
  const [f, setF] = useState({ name: "", contact: "", plan: "", since: "" });
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return;
    setBusy(true);
    try {
      const { client } = await api.post<{ client: { id: string } }>("/clients", {
        name: f.name.trim(),
        contact: f.contact,
        plan: f.plan,
        since: f.since,
      });
      onSaved(client?.id);
      onClose();
      setF({ name: "", contact: "", plan: "", since: "" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title="New client"
      description="Create the client, then add projects and team members to it."
      accent
      width={520}
      onClose={onClose}
      footer={
        <div className="row-8">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || !f.name.trim()}>
            {busy ? "Creating…" : "Create client"}
          </Button>
        </div>
      }
    >
      <div className="stack-16">
        <Field label="Name" required>
          <Input
            value={f.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Spillcraft Ltd"
          />
        </Field>
        <Field label="Main contact">
          <Input value={f.contact} onChange={(e) => set("contact", e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field label="Plan" hint="e.g. Retainer · 20h/mo">
            <Input value={f.plan} onChange={(e) => set("plan", e.target.value)} />
          </Field>
          <Field label="Client since" hint="e.g. Mar 2025">
            <Input value={f.since} onChange={(e) => set("since", e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
